import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePostMessage } from 'spectacle-ui-game-bridge-react';
import { useState, useEffect, useCallback, createRef } from 'react';
import { getPath } from '../../config';
import { PreloginContent } from '../../hooks/useContentForBuild';
import { usePreloginContext } from '../../context/PreloginContext';
import CloseIcon from '../../icons/CloseIcon';
import Loader from '../../components/loader/loader';
import Modal from '../../components/modal/modal';
import useBackButton from '../../hooks/useBackButton';
import useDlogLoginFunnel from '../../hooks/useDlogLoginFunnel';
import useFocusInputs from '../../hooks/useFocusInputs';
import './guestLogin.sass';

type GuestLoginComponent = {
  content: PreloginContent | null;
  isShown: boolean;
  toggleIsShown: () => void;
};

type Error = {
  message: string;
};

export default function GuestLogin({ content, isShown, toggleIsShown }: GuestLoginComponent) {
  const navigate = useNavigate();
  const { hasValidSoftAccountUsername, accountRegistrationStatus } = usePreloginContext();
  const [error, setError] = useState<Error | null>(null);
  const [isShowingLoader, setIsShowingLoading] = useState<boolean>(false);
  const createSoftAccount = usePostMessage('createSoftAccount');
  const closeWebView = usePostMessage('closeWebView');
  const passPreloginFence = usePostMessage('passPreloginFence');
  const checkUsernameString = usePostMessage('checkUsernameString');
  const dlog = useDlogLoginFunnel();
  const formElement = createRef<HTMLFormElement>();
  const [setOffset, offsetStyle, onFocus, onBlur] = useFocusInputs();

  useBackButton(isShown, toggleIsShown);

  const [form, setForm] = useState({
    username: '',
  });

  const usernameInputRef = useCallback((node: any) => {
    if (node !== null) {
      setOffset('username', node.getBoundingClientRect().top);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const UserNameRulesConstants = {
    MIN_LENGTH: 2, // Represents the UTF-8 min/max bytes for username
    MAX_LENGTH: 65, // Leave room for null terminator
    GLYPH_BYTE_LIMIT: 3,
    MIN_GLYPHS: 2,
    MAX_GLYPHS: 16, // Max username length is 16 characters
    MAX_GLYPHS_WITH_NUMBER: 24, // MAX_GLYPHS + 1 for # + 7 for the number after the hash
  };

  const isSubmitDisabled = form.username.length < UserNameRulesConstants.MIN_GLYPHS || isShowingLoader;

  const getByteSize = (str: string) => {
    const escstr = encodeURIComponent(str);
    const binstr = escstr.replace(/%([0-9A-F]{2})/gi, function (match, hex) {
      const i = parseInt(hex, 16);
      return String.fromCharCode(i);
    });

    return binstr.length;
  };

  const getCharSize = (str: string) => {
    let point;
    let index;
    let width = 0;
    let len = 0;

    for (index = 0; index < str.length; ) {
      point = str.codePointAt(index);
      width = 0;
      while (point) {
        width += 1;
        point = point >> 8;
      }
      index += Math.round(width / 2);
      len += 1;
    }

    return len;
  };

  const handleGuestLogin = (e: any) => {
    onBlur();
    e.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    if (!content) {
      return;
    }

    dlog({
      phase: 'Login_Guest_Creation_Screen',
      action: 'Click',
      targets: 'Create',
    });

    // Check Byte Length + Glyph Count
    const byteSize = getByteSize(form.username);
    const glyphCount = getCharSize(form.username);

    if (byteSize < UserNameRulesConstants.MIN_LENGTH || byteSize > UserNameRulesConstants.MAX_LENGTH || glyphCount < UserNameRulesConstants.MIN_GLYPHS || glyphCount > UserNameRulesConstants.MAX_GLYPHS) {
      setError({
        message: content.usernameRequiredLength.replace('&&1', UserNameRulesConstants.MIN_GLYPHS.toString()).replace('&&2', UserNameRulesConstants.MAX_GLYPHS.toString()),
      });

      return;
    }

    // Ensure Username String is Passing
    if (!hasValidSoftAccountUsername) {
      setError({
        message: content.usernameReqSingleGlyphLimit,
      });

      return;
    }

    // All Passing
    if (hasValidSoftAccountUsername) {
      // Create the Account
      setIsShowingLoading(true);
      createSoftAccount(form.username);
    }
  };

  const handleFormChange = (e: React.FormEvent<any>) => {
    const target = e.target as any;
    const updateTarget = target.name;

    // Don't allow spaces
    if (target.value.slice(-1) === ' ') {
      return;
    }

    // Check if We have a Valid Username
    checkUsernameString(target.value);

    setForm((prev: any) => ({
      ...prev,
      [updateTarget]: target.value,
    }));
  };

  const clearError = (e: React.FormEvent<any>) => {
    e.preventDefault();
    setError(null);
  };

  useEffect(() => {
    if (isShown && content) {
      if (accountRegistrationStatus === 'AccountRegistrationSuccess') {
        passPreloginFence();
        dlog({
          phase: 'Login_Guest_Creation_Screen',
          action: 'Success',
        });
        if (content?.enablePostLoginMessages) {
          navigate('/post-login');
        } else {
          closeWebView();
        }
      }

      if (accountRegistrationStatus === 'AccountRegistrationInvalidUsername') {
        setError({
          message: content.inappropriateText,
        });
      }

      if (accountRegistrationStatus === 'AccountRegistrationFailure') {
        setError({
          message: content.signInError,
        });
      }

      setIsShowingLoading(false);
    }
  }, [accountRegistrationStatus, passPreloginFence, closeWebView, isShown, content, navigate, dlog]);

  useEffect(() => {
    if (isShown) {
      dlog({
        phase: 'Login_Guest_Creation_Screen',
        action: 'Render',
      });
    }
  }, [isShown, dlog]);

  useEffect(() => {
    if (error) {
      dlog({
        phase: 'Login_Guest_Creation_Screen',
        action: 'Error',
      });
    }
  }, [error, dlog]);

  return (
    <AnimatePresence>
      {isShown && content && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="guest-login" style={{ background: `url(${getPath('bg_general')}) no-repeat top center fixed #000` }}>
          <div className="guest-login__container" style={offsetStyle}>
            <h2>{content.guestLoginHeader}</h2>
            <p>{content.guestLoginDesc}</p>
            {error && (
              <Modal toggle={clearError}>
                <div className="error">
                  <h3>{content.error}</h3>
                  <p>{error.message}</p>
                  <button className="cta cta--full" onClick={clearError}>
                    {content.ok}
                  </button>
                </div>
              </Modal>
            )}
            <form ref={formElement} onSubmit={handleGuestLogin} noValidate autoComplete="off">
              <input type="text" name="username" maxLength={UserNameRulesConstants.MAX_GLYPHS} value={form.username} placeholder={content.guestLoginPlaceholder} onChange={handleFormChange} ref={usernameInputRef} onFocus={onFocus} onBlur={onBlur} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" disabled={isShowingLoader} />
              <div className="guest-login__limit">{content.guestLoginCharacterLimit}</div>
              <button className="cta cta--wide" type="submit" disabled={isSubmitDisabled}>
                {content.guestLoginCTA}
              </button>
            </form>
          </div>
          <Loader isShown={isShowingLoader} />
          <button className="close" onClick={toggleIsShown} disabled={isShowingLoader}>
            <CloseIcon className="external-link__icon" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
